/**
 * User Detail Page
 * Enterprise-grade user profile view with tabs.
 * 
 * Features:
 * - User profile header with status and actions
 * - 4 Tabs: Profile, Login History, Activity, Sessions
 * - Edit profile functionality
 * - Session management
 * - Login history audit trail
 * 
 * Design Patterns:
 * - Google Admin Console: User detail layout
 * - Amazon IAM: Security and session views
 * - Meta Admin: Activity tracking
 * 
 * AS9100D Compliance:
 * - Full audit trail visibility
 * - Session tracking for security
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Shield, Mail, Phone, Building2, Briefcase,
  Calendar, Clock, Monitor, Globe, MapPin, X, Loader2,
  MoreHorizontal, Lock, Unlock, Ban, UserCheck, KeyRound,
  LogOut, Edit2, Save, AlertCircle, CheckCircle, XCircle,
  RefreshCw, History, Activity, Smartphone, Laptop, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import userService, { 
  USER_ROLES, 
  STATUS_CONFIG, 
  getRoleConfig, 
  getStatusConfig,
  formatRelativeTime,
  generateSecurePassword,
  getPasswordStrength
} from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { formatISTDate, formatIST } from '../utils/timezone';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

// Status Badge
const StatusBadge = ({ status, size = 'md' }) => {
  const config = getStatusConfig(status);
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-medium ${config.color} ${sizeClasses[size]}`}>
      <span className="text-[10px]">{config.icon}</span>
      {config.label}
    </span>
  );
};

// Role Badge
const RoleBadge = ({ role }) => {
  const config = getRoleConfig(role);
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium ${config.color}`}>
      <Shield className="w-3.5 h-3.5 mr-1.5" />
      {config.label}
    </span>
  );
};

// User Avatar
const UserAvatar = ({ firstName, lastName, email, size = 'lg' }) => {
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || email?.[0]?.toUpperCase() || '?';
  const sizeClasses = {
    md: 'w-12 h-12 text-lg',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-20 h-20 text-2xl',
  };
  return (
    <div className={`rounded-full bg-[#101957] flex items-center justify-center text-white font-semibold ${sizeClasses[size]}`}>
      {initials}
    </div>
  );
};

// Info Row
const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
    {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5" />}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-slate-900 mt-0.5 truncate">{value || '—'}</p>
    </div>
  </div>
);

// Login Event Badge
const LoginEventBadge = ({ eventType }) => {
  const configs = {
    login_success: { label: 'Login', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    login_failure: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
    logout_manual: { label: 'Logout', color: 'bg-blue-100 text-blue-800', icon: LogOut },
    logout_timeout: { label: 'Timeout', color: 'bg-orange-100 text-orange-800', icon: Clock },
    session_expired: { label: 'Expired', color: 'bg-orange-100 text-orange-800', icon: Clock },
    forced_logout: { label: 'Forced', color: 'bg-purple-100 text-purple-800', icon: Ban },
    password_changed: { label: 'Password', color: 'bg-cyan-100 text-cyan-800', icon: KeyRound },
    password_reset: { label: 'Reset', color: 'bg-cyan-100 text-cyan-800', icon: KeyRound },
    account_locked: { label: 'Locked', color: 'bg-red-100 text-red-800', icon: Lock },
    account_unlocked: { label: 'Unlocked', color: 'bg-green-100 text-green-800', icon: Unlock },
  };
  
  const config = configs[eventType] || configs.login_success;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Device Icon
const DeviceIcon = ({ userAgent }) => {
  if (!userAgent) return <Monitor className="w-4 h-4" />;
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return <Smartphone className="w-4 h-4" />;
  }
  return <Laptop className="w-4 h-4" />;
};

// ============================================================================
// PROFILE TAB
// ============================================================================

const ProfileTab = ({ user, onEdit, isEditing, editData, setEditData, onSave, isSaving }) => {
  const roleConfig = getRoleConfig(user.role);
  
  if (isEditing) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-4 h-4" />
            Personal Information
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editData.first_name || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editData.last_name || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={editData.display_name || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editData.phone || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+91 98765 43210"
              />
            </div>
          </div>
        </Card>
        
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Organization
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                value={editData.department || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, department: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input
                value={editData.job_title || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, job_title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={editData.role || user.role}
                onValueChange={(value) => setEditData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(USER_ROLES).map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label} ({role.actionCount} actions)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
        
        <div className="lg:col-span-2 flex justify-end gap-3">
          <Button variant="outline" onClick={() => onEdit(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving} className="bg-[#101957] hover:bg-[#101957]/90 text-white">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-4 h-4" />
            Personal Information
          </h3>
          <Button variant="ghost" size="sm" onClick={() => onEdit(true)}>
            <Edit2 className="w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-1">
          <InfoRow label="Full Name" value={user.full_name} />
          <InfoRow label="Email" value={user.email} icon={Mail} />
          <InfoRow label="Phone" value={user.phone} icon={Phone} />
          <InfoRow label="Display Name" value={user.display_name} />
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Organization
        </h3>
        <div className="space-y-1">
          <InfoRow label="Department" value={user.department} icon={Building2} />
          <InfoRow label="Job Title" value={user.job_title} icon={Briefcase} />
          <div className="flex items-start gap-3 py-3 border-b border-slate-100">
            <Shield className="w-4 h-4 text-slate-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Role</p>
              <div className="flex items-center gap-2 mt-1">
                <RoleBadge role={user.role} />
                <span className="text-xs text-slate-500">({roleConfig.actionCount} actions)</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">{roleConfig.description}</p>
            </div>
          </div>
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4" />
          Security
        </h3>
        <div className="space-y-1">
          <div className="flex items-start gap-3 py-3 border-b border-slate-100">
            <AlertCircle className="w-4 h-4 text-slate-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Failed Login Attempts</p>
              <p className={`mt-0.5 ${user.failed_login_attempts > 0 ? 'text-red-600 font-medium' : 'text-slate-900'}`}>
                {user.failed_login_attempts || 0}
              </p>
            </div>
          </div>
          <InfoRow 
            label="Password Changed" 
            value={user.password_changed_at ? formatRelativeTime(user.password_changed_at) : 'Never'} 
          />
          <InfoRow 
            label="Must Change Password" 
            value={user.must_change_password ? 'Yes (on next login)' : 'No'} 
          />
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Account
        </h3>
        <div className="space-y-1">
          <InfoRow 
            label="Created" 
            value={user.created_at ? formatISTDate(user.created_at) : '—'} 
            icon={Calendar}
          />
          <InfoRow 
            label="Last Login" 
            value={user.last_login_at ? formatRelativeTime(user.last_login_at) : 'Never'} 
            icon={Clock}
          />
          {user.invitation_sent_at && (
            <InfoRow 
              label="Invitation Sent" 
              value={formatRelativeTime(user.invitation_sent_at)} 
              icon={Mail}
            />
          )}
          {user.invitation_accepted_at && (
            <InfoRow 
              label="Invitation Accepted" 
              value={formatRelativeTime(user.invitation_accepted_at)} 
              icon={CheckCircle}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

// ============================================================================
// LOGIN HISTORY TAB
// ============================================================================

const LoginHistoryTab = ({ userId }) => {
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userService.getLoginHistory(userId, {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      });
      setHistory(response.history || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Error fetching login history:', err);
      toast.error('Failed to load login history');
    } finally {
      setIsLoading(false);
    }
  }, [userId, page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="p-12 text-center">
        <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">No login history available</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <History className="w-4 h-4" />
          Login History
          <span className="text-slate-400 font-normal">({total} events)</span>
        </h3>
      </div>
      <div className="divide-y">
        {history.map((event) => (
          <div key={event.history_id} className="p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-full ${event.success ? 'bg-green-100' : 'bg-red-100'}`}>
                  {event.success ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <LoginEventBadge eventType={event.event_type} />
                    {event.failure_reason && (
                      <span className="text-xs text-red-600">({event.failure_reason})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    {event.ip_address && (
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {event.ip_address}
                      </span>
                    )}
                    {event.geo_city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.geo_city}, {event.geo_country}
                      </span>
                    )}
                    {event.user_agent && (
                      <span className="flex items-center gap-1">
                        <DeviceIcon userAgent={event.user_agent} />
                        <span className="truncate max-w-[200px]">
                          {event.user_agent.split(' ')[0]}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right text-xs text-slate-500">
                <div>{formatRelativeTime(event.created_at)}</div>
                <div className="text-slate-400">
                  {formatIST(event.created_at, { includeDate: false, includeSeconds: false })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {total > pageSize && (
        <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
          <span className="text-sm text-slate-500">
            Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page * pageSize >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// SESSIONS TAB
// ============================================================================

const SessionsTab = ({ userId, onTerminateSession, onTerminateAll }) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [terminatingId, setTerminatingId] = useState(null);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userService.getUserSessions(userId);
      setSessions(response.sessions || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      toast.error('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleTerminate = async (sessionId) => {
    setTerminatingId(sessionId);
    try {
      await userService.terminateSession(userId, sessionId);
      toast.success('Session terminated');
      fetchSessions();
    } catch (err) {
      toast.error('Failed to terminate session');
    } finally {
      setTerminatingId(null);
    }
  };

  const handleTerminateAll = async () => {
    if (onTerminateAll) {
      await onTerminateAll();
      fetchSessions();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card className="p-12 text-center">
        <Monitor className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">No active sessions</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Monitor className="w-4 h-4" />
          Active Sessions
          <span className="text-slate-400 font-normal">({sessions.length})</span>
        </h3>
        {sessions.length > 1 && (
          <Button variant="outline" size="sm" onClick={handleTerminateAll}>
            <LogOut className="w-4 h-4 mr-2" />
            Terminate All
          </Button>
        )}
      </div>
      
      <div className="grid gap-4">
        {sessions.map((session) => (
          <Card key={session.session_id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-slate-100 rounded-lg">
                  <DeviceIcon userAgent={session.user_agent} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">
                      {session.user_agent?.split(' ')[0] || 'Unknown Device'}
                    </span>
                    {session.is_current && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                    {session.ip_address && (
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {session.ip_address}
                      </span>
                    )}
                    {session.geo_city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {session.geo_city}, {session.geo_country}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Started {formatRelativeTime(session.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Activity className="w-3 h-3" />
                      Active {formatRelativeTime(session.last_activity_at)}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTerminate(session.session_id)}
                disabled={terminatingId === session.session_id}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {terminatingId === session.session_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// ACTIVITY TAB (Placeholder for now)
// ============================================================================

const ActivityTab = ({ userId }) => {
  // This would show user's actions in the system
  // For now, we'll show a placeholder
  return (
    <Card className="p-12 text-center">
      <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
      <p className="text-slate-500">Activity tracking coming soon</p>
      <p className="text-xs text-slate-400 mt-2">
        This will show the user&apos;s actions across the system
      </p>
    </Card>
  );
};

// ============================================================================
// RESET PASSWORD MODAL
// ============================================================================

const ResetPasswordModal = ({ open, onOpenChange, user, onReset }) => {
  const [mode, setMode] = useState('email');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const generatePassword = () => {
    setPassword(generateSecurePassword(16));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (mode === 'manual' && password.length < 12) {
        toast.error('Password must be at least 12 characters');
        return;
      }
      await onReset(user.user_id, mode, mode === 'manual' ? password : null);
      onOpenChange(false);
      setPassword('');
      setMode('email');
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsLoading(false);
    }
  };

  const strength = getPasswordStrength(password);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Reset Password
          </DialogTitle>
          <DialogDescription>
            Reset password for {user?.full_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Reset Method</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Send reset email
                  </div>
                </SelectItem>
                <SelectItem value="manual">
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4" />
                    Set password manually
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === 'email' ? (
            <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
              <Mail className="w-4 h-4 inline mr-2" />
              A password reset link will be sent to <strong>{user?.email}</strong>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>New Password</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generatePassword}
                    className="text-xs"
                  >
                    Generate
                  </Button>
                </div>
                <Input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 12 characters"
                />
                {password && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${strength.color} transition-all`}
                        style={{ width: `${(strength.score / 6) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500">{strength.label}</span>
                  </div>
                )}
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                User will be required to change this password on next login
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || (mode === 'manual' && password.length < 12)}
            className="bg-[#101957] hover:bg-[#101957]/90 text-white"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : mode === 'email' ? (
              <Mail className="w-4 h-4 mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {mode === 'email' ? 'Send Reset Email' : 'Set Password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // State
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch user
  const fetchUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await userService.getUser(userId);
      setUser(data);
      setEditData({
        first_name: data.first_name,
        last_name: data.last_name,
        display_name: data.display_name,
        phone: data.phone,
        department: data.department,
        job_title: data.job_title,
        role: data.role,
      });
    } catch (err) {
      console.error('Error fetching user:', err);
      toast.error('Failed to load user');
      navigate('/admin/user');
    } finally {
      setIsLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Save edit
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await userService.updateUser(userId, editData);
      setUser(updated);
      setIsEditing(false);
      toast.success('User updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  // Actions
  const handleAction = async (action) => {
    const actions = {
      suspend: {
        title: `Suspend ${user.full_name}?`,
        description: 'User will not be able to log in. All sessions will be terminated.',
        confirmLabel: 'Suspend',
        variant: 'destructive',
        fn: () => userService.suspendUser(userId),
      },
      reactivate: {
        title: `Reactivate ${user.full_name}?`,
        description: 'User will be able to log in again.',
        confirmLabel: 'Reactivate',
        fn: () => userService.reactivateUser(userId),
      },
      unlock: {
        title: `Unlock ${user.full_name}?`,
        description: 'Failed login counter will be reset.',
        confirmLabel: 'Unlock',
        fn: () => userService.unlockUser(userId),
      },
      deactivate: {
        title: `Deactivate ${user.full_name}?`,
        description: 'User will be soft-deleted but can be restored later.',
        confirmLabel: 'Deactivate',
        variant: 'destructive',
        fn: () => userService.deactivateUser(userId),
      },
      forceLogout: {
        title: `Force logout ${user.full_name}?`,
        description: 'All active sessions will be terminated.',
        confirmLabel: 'Force Logout',
        variant: 'destructive',
        fn: () => userService.forceLogoutAll(userId),
      },
      resendInvite: {
        title: `Resend invitation?`,
        description: `A new invitation email will be sent to ${user.email}`,
        confirmLabel: 'Resend',
        fn: () => userService.resendInvitation(userId),
      },
    };

    setConfirmDialog(actions[action]);
  };

  const executeAction = async () => {
    if (!confirmDialog?.fn) return;
    setActionLoading(true);
    try {
      await confirmDialog.fn();
      toast.success('Action completed');
      fetchUser();
      setConfirmDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (userId, mode, password) => {
    await userService.resetPassword(userId, mode, password);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#101957] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <p className="text-slate-500">User not found</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig(user.status);
  const roleConfig = getRoleConfig(user.role);

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="user-detail-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/user')}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Users
              </Button>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetModalOpen(true)}
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user.status === 'invited' && (
                    <DropdownMenuItem onClick={() => handleAction('resendInvite')}>
                      <Mail className="w-4 h-4 mr-2" />
                      Resend Invitation
                    </DropdownMenuItem>
                  )}
                  {user.status === 'locked' && (
                    <DropdownMenuItem onClick={() => handleAction('unlock')}>
                      <Unlock className="w-4 h-4 mr-2" />
                      Unlock Account
                    </DropdownMenuItem>
                  )}
                  {user.status === 'active' && (
                    <DropdownMenuItem onClick={() => handleAction('forceLogout')}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Force Logout All
                    </DropdownMenuItem>
                  )}
                  {['active', 'locked'].includes(user.status) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleAction('suspend')}>
                        <Ban className="w-4 h-4 mr-2" />
                        Suspend User
                      </DropdownMenuItem>
                    </>
                  )}
                  {['suspended', 'deactivated'].includes(user.status) && (
                    <DropdownMenuItem onClick={() => handleAction('reactivate')}>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Reactivate
                    </DropdownMenuItem>
                  )}
                  {user.user_id !== currentUser?.user_id && user.status !== 'invited' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleAction('deactivate')}
                        className="text-red-600 focus:text-red-600"
                      >
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Deactivate
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <UserAvatar
              firstName={user.first_name}
              lastName={user.last_name}
              email={user.email}
              size="xl"
            />
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#101957]">{user.full_name}</h1>
                <StatusBadge status={user.status} />
              </div>
              <p className="text-slate-500 mt-1">{user.email}</p>
              <div className="flex items-center gap-4 mt-3">
                <RoleBadge role={user.role} />
                {user.department && (
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    {user.department}
                  </span>
                )}
                {user.job_title && (
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    {user.job_title}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Login History
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="sessions" className="flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Sessions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab
              user={user}
              isEditing={isEditing}
              onEdit={setIsEditing}
              editData={editData}
              setEditData={setEditData}
              onSave={handleSave}
              isSaving={isSaving}
            />
          </TabsContent>

          <TabsContent value="history">
            <LoginHistoryTab userId={userId} />
          </TabsContent>

          <TabsContent value="activity">
            <ActivityTab userId={userId} />
          </TabsContent>

          <TabsContent value="sessions">
            <SessionsTab
              userId={userId}
              onTerminateAll={() => handleAction('forceLogout')}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        open={resetModalOpen}
        onOpenChange={setResetModalOpen}
        user={user}
        onReset={handleResetPassword}
      />

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{confirmDialog?.title}</DialogTitle>
            <DialogDescription>{confirmDialog?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConfirmDialog(null)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant={confirmDialog?.variant || 'default'}
              onClick={executeAction}
              disabled={actionLoading}
              className={confirmDialog?.variant !== 'destructive' ? 'bg-[#101957] hover:bg-[#101957]/90' : ''}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {confirmDialog?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDetailPage;
